import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, InputLabel, LinearProgress, MenuItem, Select, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { footerTemplatesAPI } from '../../services/api';
import Panel from '../crt/Panel';
import SectionTitle from '../crt/SectionTitle';

/** Footer-template editor + bulk-rewriter UI.
 *
 * Shows the live current template (loaded from seo.footer_templates),
 * lets the user edit the footer body + cast block + UTM query in
 * textareas, save (creates a new version row), preview rendered output
 * with sample data, and bulk-apply to Megaphone episodes.
 *
 * Body uses Python-style format slots: {episode_url}, {utm_query},
 * {episode_title} — backend validates these are present before saving.
 */
// Default template per site. SL uses 'standard_v2' (the OG canonical
// template applied to the 290 SL episodes). MB uses
// 'standard_v2_mallbrats' (mirror structure, Spout Lore as cross-promo).
const DEFAULT_TEMPLATE_BY_SITE: Record<string, string> = {
  spoutlore: 'standard_v2',
  mallbrats: 'standard_v2_mallbrats',
};

const FooterTemplatesTab: React.FC = () => {
  const qc = useQueryClient();
  const [site, setSite] = useState<'spoutlore' | 'mallbrats'>('spoutlore');
  const [templateKey, setTemplateKey] = useState(DEFAULT_TEMPLATE_BY_SITE['spoutlore']);

  // Auto-flip the template when the site changes so the user doesn't
  // have to remember to also change the template dropdown. Bypassed
  // if they explicitly picked a different template themselves (we
  // re-set whenever site changes — fine because the template list
  // shows ALL templates regardless of site, so they can re-pick).
  useEffect(() => {
    const def = DEFAULT_TEMPLATE_BY_SITE[site];
    if (def && def !== templateKey) setTemplateKey(def);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site]);

  // Edit-state for the textareas, separate from the loaded template
  // so the user can edit freely without auto-save churn.
  const [draftFooter, setDraftFooter] = useState('');
  const [draftCast, setDraftCast] = useState('');
  const [draftExtra, setDraftExtra] = useState('');
  const [draftUtm, setDraftUtm] = useState('');
  const [editing, setEditing] = useState(false);

  // Bulk-apply state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [singleEpId, setSingleEpId] = useState<string | undefined>(undefined);
  const [message, setMessage] = useState<string | null>(null);

  const { data: templatesData } = useQuery({
    queryKey: ['footer-templates'],
    queryFn: footerTemplatesAPI.list,
    staleTime: 60_000,
  });
  const templates = templatesData?.templates || [];
  // Surface the response shape in console for quick debugging when the
  // dropdown is blank — past versions of the API shipped a different
  // field name and the default-empty fallback above would hide it.
  // eslint-disable-next-line no-console
  if (templatesData) console.debug('footer templates response:', templatesData);

  const { data: current, isFetching: currentLoading } = useQuery({
    queryKey: ['footer-template-current', templateKey],
    queryFn: () => footerTemplatesAPI.get(templateKey),
    enabled: !!templateKey,
    staleTime: 30_000,
  });

  // When the loaded template changes (different key, or just refreshed
  // after a save), reset the draft to match. Skip if the user is mid-
  // edit so we don't clobber their typing.
  useEffect(() => {
    if (current && !editing) {
      setDraftFooter(current.footer_body_html || '');
      setDraftCast(current.cast_block_html || '');
      setDraftExtra(current.extra_html || '');
      setDraftUtm(current.utm_query || '');
    }
  }, [current, editing]);

  const { data: preview } = useQuery({
    queryKey: ['footer-template-preview', templateKey, current?.version],
    queryFn: () => footerTemplatesAPI.preview(templateKey),
    enabled: !!current,
    staleTime: 30_000,
  });

  const { data: plan, isFetching: planLoading } = useQuery({
    queryKey: ['footer-template-plan', templateKey, current?.version, site],
    queryFn: () => footerTemplatesAPI.plan(templateKey, site),
    enabled: !!current && !!site,
    staleTime: 30_000,
  });

  const saveMut = useMutation({
    mutationFn: () => footerTemplatesAPI.save(templateKey, {
      footer_body_html: draftFooter,
      cast_block_html: draftCast,
      extra_html: draftExtra,
      utm_query: draftUtm,
    }),
    onSuccess: (data) => {
      setMessage(`Saved as version ${data.version}`);
      setEditing(false);
      qc.invalidateQueries({ queryKey: ['footer-template-current'] });
      qc.invalidateQueries({ queryKey: ['footer-template-preview'] });
      qc.invalidateQueries({ queryKey: ['footer-template-plan'] });
      qc.invalidateQueries({ queryKey: ['footer-templates'] });
    },
    onError: (e: any) => setMessage(`Save error: ${e?.response?.data?.detail || e.message}`),
  });

  const applyMut = useMutation({
    mutationFn: (params: { episode_id?: string }) =>
      footerTemplatesAPI.apply(templateKey, site, params.episode_id),
    onSuccess: (data) => {
      setMessage(
        `Applied ${data.applied} • Failed ${data.failed}` +
        (data.failed > 0 ? ` — see browser console for details` : '')
      );
      if (data.failed > 0) console.error('Footer apply failures:', data.failures);
      setConfirmOpen(false);
      setConfirmText('');
      setSingleEpId(undefined);
      qc.invalidateQueries({ queryKey: ['footer-template-plan'] });
    },
    onError: (e: any) => setMessage(`Error: ${e?.response?.data?.detail || e.message}`),
  });

  const items = plan?.items || [];
  const actionable = items.filter((i: any) => i.will_change);
  const noFooter = plan?.no_footer_count ?? 0;
  const unchanged = plan?.unchanged_count ?? 0;

  const isDirty =
    !!current && (
      draftFooter !== current.footer_body_html ||
      draftCast   !== current.cast_block_html ||
      draftExtra  !== (current.extra_html || '') ||
      draftUtm    !== current.utm_query
    );

  return (
    <Box>
      {message && (
        <Alert severity={message.startsWith('Error') || message.startsWith('Save error') ? 'error' : 'success'}
               onClose={() => setMessage(null)} sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}

      <Panel label="FOOTER TEMPLATES">
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel>Template</InputLabel>
            <Select label="Template" value={templateKey}
                    onChange={(e) => { setTemplateKey(e.target.value); setEditing(false); }}>
              {templates.map((t: any) => (
                <MenuItem key={t.template_key} value={t.template_key}>
                  {t.name}  (v{t.version})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Podcast feed</InputLabel>
            <Select label="Podcast feed" value={site} onChange={(e) => setSite(e.target.value as any)}>
              <MenuItem value="spoutlore">Spout Lore (Megaphone)</MenuItem>
              <MenuItem value="mallbrats">Mall Brats (Megaphone)</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            color="warning"
            disabled={!actionable.length || applyMut.isPending}
            onClick={() => { setSingleEpId(undefined); setConfirmOpen(true); }}
          >
            Apply to {actionable.length} episode{actionable.length === 1 ? '' : 's'}
          </Button>
        </Box>

        {(planLoading || currentLoading) && <LinearProgress sx={{ mt: 2 }} />}

        <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
          <Chip color="warning" label={`${actionable.length} actionable`} />
          <Chip color="default" label={`${unchanged} already canonical`} />
          <Chip color="info" label={`${noFooter} no footer (skipped)`} />
          {current && <Chip variant="outlined" label={`Active version: ${current.version}`} />}
        </Box>
      </Panel>

      <Panel label="EDIT FOOTER" sx={{ mt: 3 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          The body uses three substitution slots that get filled per episode at apply time:
          <strong> &#123;episode_url&#125;</strong> (full WP URL),{' '}
          <strong>&#123;utm_query&#125;</strong> (the value below),{' '}
          <strong>&#123;episode_title&#125;</strong> (Megaphone clean title).
        </Typography>
        <TextField
          label="Footer body HTML"
          multiline minRows={10} maxRows={20} fullWidth
          value={draftFooter}
          onChange={(e) => { setDraftFooter(e.target.value); setEditing(true); }}
          sx={{ mb: 2, '& textarea': { fontFamily: 'monospace', fontSize: '0.78rem' } }}
        />
        <TextField
          label="Cast block HTML"
          multiline minRows={5} maxRows={12} fullWidth
          value={draftCast}
          onChange={(e) => { setDraftCast(e.target.value); setEditing(true); }}
          sx={{ mb: 2, '& textarea': { fontFamily: 'monospace', fontSize: '0.78rem' } }}
        />
        <TextField
          label="Extra HTML (e.g. Special Thanks / music credits — optional)"
          multiline minRows={3} maxRows={10} fullWidth
          value={draftExtra}
          onChange={(e) => { setDraftExtra(e.target.value); setEditing(true); }}
          sx={{ mb: 2, '& textarea': { fontFamily: 'monospace', fontSize: '0.78rem' } }}
          helperText="Inserted between the footer and the Cast section. Mall Brats: Special Thanks to Samuel Quinn Morris and Aaron Charles Read for the theme music + Taylor Swindells for the score. Spout Lore: empty by default."
        />
        <TextField
          label="UTM query string"
          fullWidth size="small"
          value={draftUtm}
          onChange={(e) => { setDraftUtm(e.target.value); setEditing(true); }}
          sx={{ mb: 2, '& input': { fontFamily: 'monospace', fontSize: '0.78rem' } }}
          helperText="Appended to the {episode_url} placeholder. Leave default unless you have a reason."
        />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" color="primary"
                  disabled={!isDirty || saveMut.isPending}
                  onClick={() => saveMut.mutate()}>
            {saveMut.isPending ? 'Saving…' : 'Save as new version'}
          </Button>
          <Button variant="outlined"
                  disabled={!isDirty}
                  onClick={() => {
                    if (current) {
                      setDraftFooter(current.footer_body_html);
                      setDraftCast(current.cast_block_html);
                      setDraftExtra(current.extra_html || '');
                      setDraftUtm(current.utm_query);
                      setEditing(false);
                    }
                  }}>
            Reset to active
          </Button>
        </Box>
      </Panel>

      {preview && (
        <Panel label="LIVE PREVIEW (active version)" sx={{ mt: 3 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Rendered with sample episode_url + episode_title. To preview your draft edits, save first.
          </Typography>
          {/* Grid template uses minmax(0, 1fr) so each column is forced to
              equal half-width regardless of intrinsic content size — without
              the `0` min, long unbreakable URLs in the visible-rendered side
              would expand the column past 50% and squish the raw-HTML side. */}
          <Box sx={{ display: 'grid', gap: 2,
                     gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1fr)' } }}>
            <Box sx={{ minWidth: 0 }}>
              <SectionTitle>VISIBLE — AS RENDERED</SectionTitle>
              <Box sx={{ p: 2, border: '1px solid var(--crt-green, #00ff41)',
                         borderRadius: 1, fontSize: '0.85rem',
                         minWidth: 0, overflow: 'hidden',
                         // Long URLs in <a> tags don't break by default — let
                         // them wrap mid-string so the column doesn't blow out.
                         wordBreak: 'break-word', overflowWrap: 'anywhere',
                         '& a': { color: 'var(--crt-green, #00ff41)',
                                  wordBreak: 'break-all' } }}>
                <div dangerouslySetInnerHTML={{ __html: preview.rendered_footer || '' }} />
                {preview.rendered_extra && (
                  <div dangerouslySetInnerHTML={{ __html: preview.rendered_extra }} />
                )}
                <div dangerouslySetInnerHTML={{ __html: preview.rendered_cast || '' }} />
              </Box>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <SectionTitle>RAW HTML</SectionTitle>
              <Box component="pre" sx={{ fontSize: '0.7rem', m: 0, p: 2,
                                         backgroundColor: 'action.hover', borderRadius: 1,
                                         minWidth: 0,
                                         overflow: 'auto', maxHeight: 400,
                                         whiteSpace: 'pre-wrap',
                                         wordBreak: 'break-word' }}>
                {preview.rendered_footer || ''}
                {preview.rendered_extra ? '\n\n' + preview.rendered_extra : ''}
                {'\n\n'}
                {preview.rendered_cast || ''}
              </Box>
            </Box>
          </Box>
        </Panel>
      )}

      <Panel label="EPISODES" sx={{ mt: 3 }}>
        <SectionTitle>{`${plan?.total_episodes ?? '...'} EPISODES IN ${site.toUpperCase()} MEGAPHONE FEED`}</SectionTitle>
        <Box sx={{ overflowX: 'auto', mt: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>EPISODE</TableCell>
                <TableCell>STATUS</TableCell>
                <TableCell>LINK</TableCell>
                <TableCell align="right">ACTIONS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((it: any) => (
                <TableRow key={it.episode_id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {it.season != null && it.episode != null ? `S${it.season}E${it.episode}` : '—'}
                    </Typography>
                    <Typography variant="body2">{it.title}</Typography>
                  </TableCell>
                  <TableCell>
                    {it.skip_reason === 'no_footer' && <Chip size="small" color="info" label="no footer" />}
                    {!it.skip_reason && it.will_change && <Chip size="small" color="warning" label="will change" />}
                    {!it.skip_reason && !it.will_change && <Chip size="small" label="canonical" />}
                  </TableCell>
                  <TableCell>
                    {it.proposed_link ? (
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                        {it.current_link ? '✓' : '∅→'}
                        {it.proposed_link.replace(/^https?:\/\//, '')}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">no WP match</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {it.will_change && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => { setSingleEpId(it.episode_id); setConfirmOpen(true); }}
                      >
                        Apply just this
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Panel>

      {/* Apply confirmation */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm apply</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will modify the LIVE Megaphone podcast feed. Each affected
            episode triggers an admin "episode updated" email — do not bulk-apply
            unless you intend to. Subscribers are NOT pinged (only admin).
          </Alert>
          <Typography>
            Target: <strong>{singleEpId ? 'single episode' : `${actionable.length} actionable episodes`}</strong> on{' '}
            <strong>{site}</strong> with template <strong>{templateKey}</strong> v{current?.version}.
          </Typography>
          <Typography sx={{ mt: 2, mb: 1 }}>
            Type <code>APPLY</code> to confirm:
          </Typography>
          <input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            style={{ fontFamily: 'monospace', fontSize: '1rem', padding: '0.5rem', width: '100%' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConfirmOpen(false); setConfirmText(''); }}>Cancel</Button>
          <Button
            color="warning" variant="contained"
            disabled={confirmText !== 'APPLY' || applyMut.isPending}
            onClick={() => applyMut.mutate({ episode_id: singleEpId })}
          >
            {applyMut.isPending ? 'Applying…' : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FooterTemplatesTab;
